import React, { forwardRef } from 'react';
import styled from 'styled-components';

const CardWrapper = styled.div`
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 1200px;
  height: 630px;
  background: ${props => props.$gradient || 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'};
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  overflow: hidden;
  z-index: -1;
`;

const InfoSection = styled.div`
  flex: 0 0 60%;
  padding: 48px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const MapSection = styled.div`
  flex: 0 0 40%;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 60px;
    background: linear-gradient(to right, ${props => props.$bgColor || '#16213e'}, transparent);
    z-index: 1;
  }
`;

const TripTitle = styled.h1`
  font-size: 42px;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 16px 0;
  max-width: 90%;
`;

const TripMeta = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 500;
  opacity: 0.85;
`;

const HighlightsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  margin-bottom: 24px;
`;

const HighlightItem = styled.div`
  font-size: 16px;
  opacity: 0.7;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '\u25CF';
    font-size: 8px;
    opacity: 0.5;
  }
`;

const BrandStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const BrandName = styled.span`
  font-size: 16px;
  font-weight: 700;
  opacity: 0.6;
  letter-spacing: 0.5px;
`;

const PersonalityBadge = styled.span`
  font-size: 14px;
  padding: 4px 14px;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  opacity: 0.6;
`;

const getCountryEmoji = (country) => {
  const map = {
    japan: '\u{1F1EF}\u{1F1F5}', india: '\u{1F1EE}\u{1F1F3}', france: '\u{1F1EB}\u{1F1F7}',
    italy: '\u{1F1EE}\u{1F1F9}', spain: '\u{1F1EA}\u{1F1F8}', thailand: '\u{1F1F9}\u{1F1ED}',
    usa: '\u{1F1FA}\u{1F1F8}', 'united states': '\u{1F1FA}\u{1F1F8}', mexico: '\u{1F1F2}\u{1F1FD}',
    brazil: '\u{1F1E7}\u{1F1F7}', australia: '\u{1F1E6}\u{1F1FA}', germany: '\u{1F1E9}\u{1F1EA}',
    uk: '\u{1F1EC}\u{1F1E7}', 'united kingdom': '\u{1F1EC}\u{1F1E7}', greece: '\u{1F1EC}\u{1F1F7}',
    turkey: '\u{1F1F9}\u{1F1F7}', egypt: '\u{1F1EA}\u{1F1EC}', morocco: '\u{1F1F2}\u{1F1E6}',
    iceland: '\u{1F1EE}\u{1F1F8}', peru: '\u{1F1F5}\u{1F1EA}', indonesia: '\u{1F1EE}\u{1F1E9}',
    vietnam: '\u{1F1FB}\u{1F1F3}', 'south korea': '\u{1F1F0}\u{1F1F7}', portugal: '\u{1F1F5}\u{1F1F9}',
    croatia: '\u{1F1ED}\u{1F1F7}', nepal: '\u{1F1F3}\u{1F1F5}', 'new zealand': '\u{1F1F3}\u{1F1FF}',
    canada: '\u{1F1E8}\u{1F1E6}', singapore: '\u{1F1F8}\u{1F1EC}',
  };
  return map[(country || '').toLowerCase()] || '\u{1F30D}';
};

const TripCard = forwardRef(({ itineraryData, extractedCity }, ref) => {
  const title = itineraryData.title || `Trip to ${itineraryData.destination || extractedCity || 'Amazing Places'}`;
  const country = itineraryData.country || itineraryData.destination || extractedCity || '';
  const days = itineraryData.totalDays || itineraryData.days || (itineraryData.dailyItinerary || itineraryData.dayByDay || []).length;
  const budget = itineraryData.estimatedCost || itineraryData.estimatedBudget || itineraryData.budgetRange;
  const dailyItinerary = itineraryData.dailyItinerary || itineraryData.dayByDay || [];

  // Gather top 3 activity names
  const topActivities = [];
  for (const day of dailyItinerary) {
    for (const block of ['morning', 'afternoon', 'evening']) {
      for (const act of (day[block] || [])) {
        const name = typeof act === 'string' ? act : (act.activity || act.name || '');
        if (name && topActivities.length < 3) {
          const clean = name.replace(/^\d{1,2}:\d{2}\s*(AM|PM)?\s*-\s*/i, '');
          topActivities.push(clean.length > 50 ? clean.slice(0, 47) + '...' : clean);
        }
      }
    }
  }

  // Static map URL (if coordinates available)
  const placesCovered = itineraryData.placesCovered || [];
  let staticMapUrl = null;
  if (placesCovered.length > 0 && placesCovered[0].latitude) {
    const markers = placesCovered.slice(0, 10).map(p =>
      `${p.latitude},${p.longitude}`
    ).join('|');
    const center = `${placesCovered[0].latitude},${placesCovered[0].longitude}`;
    staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=6&size=480x630&maptype=roadmap&markers=color:blue|${markers}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&style=feature:all|element:geometry|color:0x242f3e&style=feature:water|color:0x17263c`;
  }

  // Travel DNA personality
  let personality = null;
  try {
    const dna = JSON.parse(localStorage.getItem('travelDNA') || 'null');
    if (dna?.travelPersonality) personality = dna.travelPersonality;
  } catch { /* ignore */ }

  const budgetLabel = typeof budget === 'number'
    ? `$${budget.toLocaleString()}`
    : typeof budget === 'string' ? budget : null;

  return (
    <CardWrapper ref={ref}>
      <InfoSection>
        <div>
          <TripTitle>{getCountryEmoji(country)} {title}</TripTitle>
          <TripMeta>
            {days > 0 && <MetaItem>{'\u{1F4C5}'} {days} days</MetaItem>}
            {budgetLabel && <MetaItem>{'\u{1F4B0}'} {budgetLabel}</MetaItem>}
          </TripMeta>
        </div>

        <HighlightsList>
          {topActivities.map((name, i) => (
            <HighlightItem key={i}>{name}</HighlightItem>
          ))}
        </HighlightsList>

        <BrandStrip>
          <BrandName>safarglimpse.com</BrandName>
          {personality && <PersonalityBadge>{personality}</PersonalityBadge>}
        </BrandStrip>
      </InfoSection>

      {staticMapUrl && (
        <MapSection>
          <img src={staticMapUrl} alt="Trip map" crossOrigin="anonymous" width={480} height={630} />
        </MapSection>
      )}
    </CardWrapper>
  );
});

TripCard.displayName = 'TripCard';
export default TripCard;
